import {
    Points,
    Mesh,
    Light,
    Camera,
    AxesHelper,
    PerspectiveCamera,
    Clock,
    Scene,
    PCFShadowMap,
    ACESFilmicToneMapping,
    Color,
    WebGLRenderer,
    AmbientLight,
    DirectionalLight,
    Vector3,
    BoxGeometry,
    MeshLambertMaterial,
    PlaneGeometry,
    DoubleSide,
} from 'three';

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

// @ts-ignore
import { FolderApi, Pane } from 'tweakpane';
import InteractiveControls from '@/ts/utils/webgl/scenes/particles/particles.scene.controls';

const BACKGROUND_COLOR = '#333';
const VERTICAL_FIELD_OF_VIEW = 45; // degrees 45 is the normal

type Actors = Points | Mesh | Light | AxesHelper | Camera;
type SideEffectTypes = 'animation' | 'gui' | 'event' | 'effect';

type LoadChain = {
    animations: Function[];
    actors: [[Actors, ...SideEffect[]]?];
};

type SideEffect = { id: SideEffectTypes; effect: Function };

//General class, with general configuration, that initialize a 3D scene;

export class BaseScene {
    public stopTime: any = null;
    public id = 'baseScene';
    public $target: HTMLElement;
    public $canvas: HTMLElement;
    public touch: any;
    public handlerInteractiveMove: any;
    public initPhases = {
        gui: false,
        camera: false,
        controls: false,
        lights: false,
        meshes: false,
        scene: false,
        events: false,
        ready: false,
    };

    public background = new Color(BACKGROUND_COLOR);
    public alpha = 0;

    private _scene: Scene = new Scene();
    private _renderer: WebGLRenderer = new WebGLRenderer({
        antialias: true,
        alpha: true,
    });
    protected _camera: PerspectiveCamera = new PerspectiveCamera(VERTICAL_FIELD_OF_VIEW, 3 / 2);
    protected _controls: OrbitControls | InteractiveControls | null = null;

    protected loadChain: LoadChain = {
        animations: [],
        actors: [],
    };

    protected _gui: { ctrl: Pane | FolderApi; view: HTMLDivElement } | undefined;

    public clock: Clock = new Clock();

    constructor($target: HTMLElement, gui?: { ctrl: Pane; view: HTMLDivElement }) {
        this.$target = $target;

        this._gui = gui;

        this._renderer.shadowMap.enabled = true;
        this._renderer.shadowMap.type = PCFShadowMap;
        this._renderer.toneMapping = ACESFilmicToneMapping;
        this._renderer.toneMappingExposure = 1;

        this.$canvas = this._renderer.domElement;

        if ($target) {
            const $el = this.$canvas;
            $target.appendChild(this.$canvas);

            this._camera = new PerspectiveCamera(VERTICAL_FIELD_OF_VIEW, $el.clientWidth / $el.clientHeight);

            this.updateRenderer();
        }
    }

    async gui(): Promise<any> {
        // Axes Helper
        this.initPhases.gui = true;
        if (this._gui) {
            document.body.appendChild(this._gui.view);

            this._gui.ctrl.addFolder({
                title: this.id,
                expanded: false,
            });

            const axesHelper = new AxesHelper();
            this.loadChain.actors.push([
                axesHelper,
                {
                    id: 'gui',
                    effect: () => {
                        this._gui?.ctrl.addBinding(axesHelper, 'visible', {
                            label: 'AxesHelper',
                        });
                    },
                },
            ]);

            this._gui.ctrl.addBinding(this._camera, 'position');
            this._gui.ctrl.addBinding(this._camera, 'rotation');

            const btn = this._gui.ctrl.addButton({
                title: 'Copy Camera',
            });
            btn.on('click', () => {
                console.log({
                    position: this._camera.position,
                    rotation: this._camera.rotation,
                });
            });

            this.loadChain.animations.push(() => {
                this._gui?.ctrl.refresh();
            });
        }
    }

    async camera(): Promise<any> {
        this.initPhases.camera = true;
        this._camera.position.set(0, 3, 8);
        this.loadChain.actors.push([this._camera]);
    }

    async controls(): Promise<any> {
        this.initPhases.controls = true;
        this._controls = new OrbitControls(this._camera, this.$canvas);
        this._controls.enableDamping = true;
    }

    async lights(): Promise<any> {
        this.initPhases.lights = true;
        const ambientLight = new AmbientLight(0xffffff, 0.8);

        const directionalLight = new DirectionalLight('#ffffff', 1);
        directionalLight.castShadow = true;
        directionalLight.shadow.mapSize.set(1024, 1024);
        directionalLight.shadow.camera.far = 15;
        directionalLight.shadow.normalBias = 0.05;
        directionalLight.position.set(0.25, 2, 2.25);

        this.loadChain.actors.push([ambientLight]);
        this.loadChain.actors.push([
            directionalLight,
            {
                id: 'gui',
                effect: () => {
                    if (this._gui) {
                        const DirectionalLightFolder = this._gui.ctrl.addFolder({
                            title: 'Directional Light',
                        });
                        Object.keys(directionalLight.position).forEach(key => {
                            DirectionalLightFolder.addBinding(directionalLight.position, key as keyof Vector3, {
                                min: -100,
                                max: 100,
                                step: 1,
                            });
                        });
                    }
                },
            },
        ]);
    }

    async meshes(): Promise<any> {
        this.initPhases.meshes = true;
        const box = new Mesh(
            new BoxGeometry(1, 1, 1, 1),
            new MeshLambertMaterial({
                color: '#7e9bbc',
            })
        );

        const plane = new Mesh(
            new PlaneGeometry(10, 10, 1, 1),
            new MeshLambertMaterial({
                color: '#808689',
                side: DoubleSide,
            })
        );

        box.position.set(0, 1, 0);
        box.castShadow = true;

        plane.receiveShadow = true;
        plane.rotation.set(-Math.PI / 2, 0, 0);
        plane.position.set(0, -0.1, 0);

        this.loadChain.actors.push([
            box,
            {
                id: 'animation',
                effect: (() => {
                    const maxHeight = 2.0;
                    let ascension = true;
                    return () => {
                        box.rotation.x -= 0.005;
                        box.rotation.y += 0.005;
                        box.rotation.z += 0.001;

                        if (box.position.y < maxHeight && ascension) {
                            box.position.y += 0.008;
                        } else if (box.position.y > 0.5 && !ascension) {
                            box.position.y -= 0.01;
                        } else {
                            ascension = !ascension;
                        }
                    };
                })(),
            },
        ]);
        this.loadChain.actors.push([plane]);
    }

    async events(): Promise<any> {
        this.initPhases.events = true;

        this.loadChain.actors.forEach(row => {
            if (row) {
                const [, ...effects] = row;
                effects.forEach(({ id, effect }) => {
                    if (id === 'event') {
                        effect.apply(this);
                    }
                });
            }
        });

        window.addEventListener('resize', this.updateRenderer);
    }

    async loadAll(): Promise<any> {
        await this.gui();

        await this.camera();
        await this.controls();
        await this.lights();
        await this.meshes();

        await this.events();

        this.scene();
    }

    async destroy() {
        this._renderer.dispose();
        this._renderer.forceContextLoss();
        this.$target.removeChild(this.$canvas);
        window.removeEventListener('resize', this.updateRenderer);
    }

    setTime(stopTime: boolean) {}

    load() {
        this.scene();
    }

    private scene() {
        this.initPhases['scene'] = true;
        this._renderer.setClearColor(new Color(this.background), this.alpha);
        this.loadChain.actors.forEach(row => {
            if (row) {
                const [actor, ...effects] = row;
                this._scene.add(actor);

                effects.forEach(({ id, effect }) => {
                    if ((id === 'gui' && this.initPhases.gui) || id === 'effect') {
                        effect.apply(this);
                    } else if (id === 'animation') {
                        //By moving animations in the scene phase, we ensure that the relevant actor is loaded as well.
                        this.loadChain.animations.push(effect);
                    }
                });
            }
        });
        this.initPhases['ready'] = true;
    }

    render() {
        if (this.initPhases['ready']) {
            if (this._controls && this._controls instanceof OrbitControls) {
                this._controls?.update();
            }

            this.loadChain.animations.forEach(animation => {
                animation.apply(this);
            });
            this._renderer.render(this._scene, this._camera);
            this.setTime(this.stopTime);
        }
    }

    updateRenderer() {
        const $el = this.$canvas;
        if ($el) {
            $el.style.width = '';
            $el.style.height = '';

            const width = $el.clientWidth;
            const height = $el.clientHeight;

            this._camera.aspect = width / height;
            this._camera.updateProjectionMatrix();
            this._renderer.setSize(width, height);
            this._renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        }
    }
}
